import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { keys } from "@shared/api";
import {
  SCREEN_CODECS,
  SCREEN_PROFILES,
  sameProfile,
  type Call,
} from "@entities/call";
import { hasAppSound, hasNativeCapture, listSoundApps } from "@shared/capture";
import { Button } from "@shared/ui/button";
import { Hint } from "@shared/ui/bits";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import { IconShare } from "@shared/ui/icons";
import { chooseCodec, chooseProfile, chooseSound, useScreenPrefs } from "../model/prefs";
import { SharePicker } from "./SharePicker";

/**
 * Показать экран — кнопка вместе со всем, что за ней стоит.
 *
 * Одна на два места: полосу голосового канала и панель идущего звонка.
 * Вынесена сюда потому, что между собой эти два места не видны — оба
 * составные, и общее у них живёт слоем ниже.
 *
 * Различаются они только видом кнопки, и это `variant`: своя плитка в полосе
 * канала и мелкий знак в панели. Подпись под кнопкой была у первой и ушла:
 * что делает знак, говорит подсказка — одна и та же на оба вида. Разметку
 * наружу не отдаём — иначе набор классов чужого экрана становится частью
 * договора.
 *
 * На экране они бывают и разом — панель звонка видна и в самом голосовом
 * канале. Спорить им не о чем: что показывать в каком качестве, помнит один
 * стор на оба места, а открыт из двух выборов всегда тот, по которому
 * щёлкнули, — «открыт ли выбор» каждая кнопка держит при себе.
 */
export function ShareButton({
  call,
  channelName,
  serverName,
  variant = "bar",
  side = "top",
}: {
  call: Call;
  /** Куда идёт показ — надпись в полноэкранном выборе источника. */
  channelName: string;
  serverName: string;
  variant?: "bar" | "dock";
  side?: "top" | "bottom";
}) {
  const [picking, setPicking] = useState(false);
  /*
   * Есть ли в этой сборке нативный захват: ответ на всё приложение один и
   * навсегда, поэтому вопрос задаётся кэшу, а не эффектом с состоянием в
   * каждом голосовом канале. `null` — ещё не спросили.
   */
  const { data: native = null } = useQuery({
    queryKey: keys.nativeCapture(),
    queryFn: hasNativeCapture,
    staleTime: Infinity,
  });
  const { profile, codec, sound } = useScreenPrefs();
  /*
   * Звук приложения — Linux-оболочка: портал звука не даёт, и чей звук
   * показывать, спрашиваем здесь, до окна выбора. Список живой — пока выбор
   * открыт, в нём появляется то, что только что заиграло.
   */
  const { data: soundOk = false } = useQuery({
    queryKey: keys.appSound(),
    queryFn: hasAppSound,
    staleTime: Infinity,
  });
  const { data: apps = [] } = useQuery({
    queryKey: keys.soundApps(),
    queryFn: listSoundApps,
    enabled: soundOk && picking && !call.screen,
    refetchInterval: 2000,
  });
  const heard = apps.find((app) => app.name === sound) ?? null;

  const label = call.screen ? "Остановить показ" : "Показать экран";

  /*
   * Показ уже идёт — кнопка его останавливает, а выбор не открывает вовсе.
   * Слою об этом нажатии знать не нужно, поэтому оно до него и не доходит.
   */
  const stopIfSharing = (event: React.MouseEvent) => {
    if (!call.screen) return;
    event.preventDefault();
    void call.stopScreen();
  };

  const trigger =
    variant === "bar" ? (
      <button
        type="button"
        className={`call-ctl ${call.screen ? "on" : ""}`}
        onClick={stopIfSharing}
      >
        <span className="call-ctl-glyph"><IconShare size={17} /></span>
      </button>
    ) : (
      <button
        type="button"
        className={`dock-act ${call.screen ? "on" : ""}`}
        onClick={stopIfSharing}
      >
        <IconShare size={15} />
      </button>
    );

  return (
    <Popover open={picking && !call.screen && !native} onOpenChange={setPicking}>
      {/*
        Окно захвата спрашивает только, что показывать. Разрешение и частоту
        не спрашивает никогда — их задаём мы, и раз так, спросить должны тоже
        мы, до окна захвата.
      */}
      <PopoverContent side={side} sideOffset={10} aria-label="Качество демонстрации">
        <div className="call-picker">
          <div className="caption" style={{ padding: "0 0 6px" }}>
            Качество
          </div>

          {SCREEN_PROFILES.map((item) => (
            <button
              key={`${item.height}p${item.fps}`}
              type="button"
              className={`call-picker-row ${sameProfile(item, profile) ? "on" : ""}`}
              onClick={() => chooseProfile(item)}
            >
              <span className="grow">
                {item.height}p{item.fps}
              </span>
              <span className="tiny muted">
                до {Math.round(item.maxBitrate / 1_000_000)} Мбит/с
              </span>
            </button>
          ))}

          <div className="caption" style={{ padding: "10px 0 6px" }}>
            Кодек
          </div>

          {SCREEN_CODECS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`call-picker-row ${codec === item.id ? "on" : ""}`}
              onClick={() => chooseCodec(item.id)}
            >
              {/* Пояснение — строкой ниже: рядом с именем в 232 пикселя оно не влезало и наползало на него. */}
              <span className="grow call-picker-text">
                <span>{item.label}</span>
                <span className="tiny muted">{item.note}</span>
              </span>
            </button>
          ))}

          {soundOk && (
            <>
              <div className="caption" style={{ padding: "10px 0 6px" }}>
                Звук
              </div>

              <div className="call-picker-list">
                <button
                  type="button"
                  className={`call-picker-row ${heard ? "" : "on"}`}
                  onClick={() => chooseSound(null)}
                >
                  <span className="grow">Без звука</span>
                </button>

                {apps.map((app) => (
                  <button
                    key={app.pid}
                    type="button"
                    className={`call-picker-row ${heard?.pid === app.pid ? "on" : ""}`}
                    onClick={() => chooseSound(app.name)}
                  >
                    <span className="grow call-picker-text">
                      <span>{app.name}</span>
                      {app.binary && app.binary !== app.name && (
                        <span className="tiny muted">{app.binary}</span>
                      )}
                    </span>
                  </button>
                ))}
              </div>

              <div className="call-picker-note">
                Звук берётся только у выбранного приложения, голоса звонка в показ не
                попадают. В списке — то, что играет звук прямо сейчас.
              </div>
            </>
          )}

          <Button
            type="button"
            size="sm"
            className="w-full"
            style={{ marginTop: 10 }}
            onClick={() => {
              setPicking(false);
              void call.startScreen(profile, codec, undefined, undefined, heard?.pid);
            }}
          >
            Показать экран
          </Button>

          <div className="call-picker-note">
            Это потолок, а не обещание: что отдаст источник, столько и будет.
            Под нехваткой полосы падает разрешение, кадры держатся.
          </div>
        </div>
      </PopoverContent>

      {/*
        В приложении выбор свой и во весь экран: там надо выбрать ещё и что
        показывать, а плитки с превью во всплывающую подсказку не помещаются.
        Веб остаётся на маленьком выборе — источник там спрашивает сам браузер.
      */}
      {picking && !call.screen && native === true && (
        <SharePicker
          channel={channelName}
          server={serverName}
          profile={profile}
          onCancel={() => setPicking(false)}
          onShare={(next, source, withSound) => {
            setPicking(false);
            chooseProfile(next);
            void call.startScreen(next, codec, source, withSound);
          }}
        />
      )}

      {/*
        Подсказка снаружи опоры, а не вокруг неё: `asChild` передаёт свойства
        и ссылку своему ребёнку, а `Hint` — обычная функция и ни того, ни
        другого не принимает. Опорой в этом порядке остаётся сама кнопка.
      */}
      <Hint label={label}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      </Hint>
    </Popover>
  );
}
